/**
 * Plain-text → React node linkifier.
 *
 * Scans a string for `http(s)://` URLs and returns an array of React nodes
 * where each URL is wrapped in an `<a target="_blank" rel="noopener
 * noreferrer">`. Non-URL spans are passed through as plain text.
 *
 * Security
 * --------
 *   - Only `http://` and `https://` schemes are linkified. `javascript:`,
 *     `data:`, `vbscript:` and other tab-nabbing or XSS-prone schemes are
 *     never turned into anchors — they stay inert text.
 *   - The `rel` attribute disables `window.opener` access from the opened
 *     tab (`noopener`) and strips the Referer header (`noreferrer`),
 *     preventing reverse-tabnabbing and leaks of the chat URL hash (which
 *     contains the shared password fragment).
 *   - The href is set via React's attribute interpolation; React handles
 *     attribute escaping. The user's input never reaches `dangerouslySet
 *     InnerHTML`, so injected HTML/script tags render as literal text.
 *
 * Behaviour
 * ---------
 *   - Trailing sentence punctuation (`.,!?;:`) is stripped from the URL
 *     and pushed back as plain text after the anchor, so "see https://
 *     example.com." links to `https://example.com` and renders the period
 *     separately.
 *   - Parentheses inside URLs (e.g. Wikipedia `..._(Bar)`) are preserved.
 *     Heuristic balancing is intentionally avoided — false positives are
 *     more annoying than a few unbalanced trailing parens in edge cases.
 *   - Whitespace inside a URL terminates it (URLs cannot contain spaces).
 */

import type { JSX } from 'react';

// `\S+` is intentionally greedy; the trailing-punctuation pass below trims
// the common sentence terminators that get absorbed by the greedy match.
const URL_RE = /https?:\/\/\S+/g;
const TRAILING_PUNCT_RE = /[.,!?;:]+$/;

export function linkify(text: string): (string | JSX.Element)[] {
  const nodes: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let key = 0;
  // `URL_RE` carries the `g` flag, so we instantiate a fresh regex each
  // call instead of reusing the module-level one to avoid `lastIndex`
  // leaking across invocations.
  const re = new RegExp(URL_RE.source, URL_RE.flags);
  for (let match = re.exec(text); match !== null; match = re.exec(text)) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    let url = match[0];
    let trailing = '';
    const trailMatch = url.match(TRAILING_PUNCT_RE);
    if (trailMatch !== null) {
      trailing = trailMatch[0];
      url = url.slice(0, url.length - trailing.length);
    }
    nodes.push(
      <a
        key={`url-${key++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="message__link"
      >
        {url}
      </a>,
    );
    if (trailing !== '') {
      nodes.push(trailing);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}
